import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { ErrorState, Skeleton } from '@/components/States';
import { ensureSession } from '@/features/auth/api';
import { orgQuery } from '@/features/org/api';
import { canSwitchOrg, pickOrgId, session } from '@/lib/auth/session';

/**
 * The signup wizard's pages: a signed-in, verified user whose org is not onboarded yet. An
 * admin or agency user never resumes a customer org's journey (legacy useOnboardingStepGuard);
 * an onboarded org goes to the app. The org is loaded here so every step reads it from cache.
 */
export const Route = createFileRoute('/_onboarding')({
  beforeLoad: async ({ location }) => {
    const s = await ensureSession();
    if (!s.user) throw redirect({ to: '/sign-in', state: { next: location.href } });
    if (!s.user.email_verified) throw redirect({ to: '/verify-email', state: { email: s.user.email } });
    if (canSwitchOrg(s.user)) throw redirect({ to: '/overview' });
    const orgId = pickOrgId(s.user);
    session.set({ orgId });
    return { user: s.user, orgId };
  },
  loader: async ({ context }) => {
    const org = await context.queryClient.query({ ...orgQuery(context.orgId), staleTime: 'static' });
    if (org.onboarding_completed) throw redirect({ to: '/overview' });
    return org;
  },
  component: Outlet,
  pendingComponent: () => (
    <div className="flex min-h-screen flex-col gap-8 p-8">
      <Skeleton className="h-8 w-28" />
      <Skeleton className="h-10 w-80" />
      <Skeleton className="h-64 w-full max-w-[900px]" />
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="flex min-h-screen items-center justify-center">
      <ErrorState error={error} onRetry={reset} />
    </div>
  ),
});
